import { cloneDeep, filter, orderBy, size } from 'lodash';
import { CodeNameModel } from 'components/common/types';
import { SortDir } from 'components/common/types/SortDir.types';
import { FormatLabel, LocationLabel } from 'store/userOptions/reducer.types';

export const FilterLabels = { ...FormatLabel, ...LocationLabel };

export const filterOptions = (
  options: Record<string, CodeNameModel[]>,
  searchText: string = '',
): Record<string, CodeNameModel[]> => {
  if (!size(options) || searchText.length < 3) return options;

  const lowercaseSearchText = searchText.toLowerCase();

  const clonedOptions = cloneDeep(options);

  Object.keys(clonedOptions).forEach((clonedOption) => {
    clonedOptions[clonedOption] = filter(clonedOptions[clonedOption], (value) => {
      const displayName = (value.nameWithMediaOwner ?? value.name).toLowerCase();
      return displayName.includes(lowercaseSearchText);
    });

    if (!clonedOptions[clonedOption].length) delete clonedOptions[clonedOption];
  });

  return clonedOptions;
};

const getCategoryLabel = (category: string): string =>
  (FilterLabels[category as keyof typeof FilterLabels] ?? category).toLowerCase();

const getCategoryOrder = (label: string, categoryOrder?: string[]): number => {
  if (!categoryOrder) return 0;

  const index = categoryOrder.findIndex((orderedLabel) => orderedLabel.toLowerCase() === label);
  return index === -1 ? categoryOrder.length : index;
};

export const sortCategories = <T>(categories: Record<string, T>, categoryOrder?: string[]): Record<string, T> => {
  const sortedEntries = Object.entries(categories).sort(([categoryA], [categoryB]) => {
    const labelA = getCategoryLabel(categoryA);
    const labelB = getCategoryLabel(categoryB);
    const orderDifference = getCategoryOrder(labelA, categoryOrder) - getCategoryOrder(labelB, categoryOrder);

    if (orderDifference !== 0) return orderDifference;

    return labelA.localeCompare(labelB);
  });

  return Object.fromEntries(sortedEntries);
};

export const sortOptions = (
  options: Record<string, CodeNameModel[]>,
  sortDir = SortDir.NONE,
  categoryOrder?: string[],
): Record<string, CodeNameModel[]> => {
  const clonedOptions = cloneDeep(options);

  if (sortDir !== SortDir.NONE) {
    Object.keys(clonedOptions).forEach((clonedOption) => {
      clonedOptions[clonedOption] = orderBy(
        clonedOptions[clonedOption],
        [(item) => item.nameWithMediaOwner ?? item.name],
        [sortDir],
      );
    });
  }

  return sortCategories(clonedOptions, categoryOrder);
};

export const hasAtLeastOneOptionResult = (options: Record<string, CodeNameModel[]>): boolean => {
  return Object.keys(options).some((option: string) => {
    const value = options[option];
    return Array.isArray(value) && value.length > 0;
  });
};

export const canDisplayOptionResults = (options?: Record<string, CodeNameModel[]>): boolean => {
  if (!options) {
    return false;
  }

  if (!hasAtLeastOneOptionResult(options)) {
    return false;
  }

  return true;
};

const isMatchingOption = (option: CodeNameModel, value: CodeNameModel): boolean =>
  option.code === value.code && option.category === value.category && option.mediaOwner === value.mediaOwner;

const updateIncludeExclude = (options: CodeNameModel[], value: CodeNameModel): CodeNameModel[] => {
  return options.map((option) => (isMatchingOption(option, value) ? { ...option, include: value.include } : option));
};

const removeOption = (options: CodeNameModel[], value: CodeNameModel): CodeNameModel[] => {
  return options.filter((option) => !isMatchingOption(option, value));
};

export const updateSelectedOptions = (options: CodeNameModel[], value: CodeNameModel): CodeNameModel[] => {
  const optionExists = options.find((option) => isMatchingOption(option, value));

  if (optionExists) {
    if (value.include !== undefined) {
      return updateIncludeExclude(options, value);
    }

    return removeOption(options, value);
  }

  return [...options, value];
};

export const isCategoryEqual = (baseCategory: string, selectedCategory?: string): boolean => {
  if (baseCategory in FilterLabels) return FilterLabels[baseCategory as keyof typeof FilterLabels] === selectedCategory;

  return baseCategory === selectedCategory;
};
